import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconHourglass, IconClose, IconMailOpen } from '../../icons';
import logger from '../../utils/logger';
import { formatDate } from '../../utils/formatters';
import {
  listMyAdvisorInvites,
  ADVISOR_EMAIL_INVITE_ENABLED,
  type SentAdvisorInvite,
} from '../../api/advisorInviteApi';

/**
 * M11b-5 — Gesendete Steuerberater-Einladungen (Owner-Sicht).
 *
 * Zeigt dem Owner die von ihm versendeten Einladungen mit Status (Ausstehend /
 * Angenommen / Abgelehnt / Abgelaufen). Liest die Daten serverseitig über die
 * Callable {@link listMyAdvisorInvites}, da `tenant_invites` clientseitig
 * via Rules nicht lesbar ist (server-only).
 *
 * Inertness: Bei Flag {@link ADVISOR_EMAIL_INVITE_ENABLED} off rendert dieses
 * Panel `null` und führt keinen Callable-Call durch. Der Server wirft bei
 * Flag-off `failed-precondition`, was von der API-Schicht als leere Liste
 * zurückgegeben wird — zusätzliche Sicherheit.
 *
 * Refreshing: Das Panel lädt erneut, wenn sich `refreshKey` ändert. Der
 * Eltern-Component (AdvisorPortalPage) erhöht den Key nach jedem erfolgreichen
 * Invite, damit die Liste sofort aktuell ist.
 *
 * @module SentInvitesPanel
 */

interface SentInvitesPanelProps {
  /**
   * Incrementing key: jedes Mal wenn der Wert sich ändert, lädt das Panel
   * die Liste neu (z.B. nach einer neuen Einladung). Entspricht React's
   * `key`-Trick ohne Unmount/Remount-Overhead.
   */
  refreshKey: number;
}

/** Maps a server status string to the corresponding i18n key. */
function statusKey(status: SentAdvisorInvite['status']): string {
  switch (status) {
    case 'accepted':
      return 'tenant.sentInvites.statusAccepted';
    case 'declined':
      return 'tenant.sentInvites.statusDeclined';
    case 'expired':
      return 'tenant.sentInvites.statusExpired';
    default:
      return 'tenant.sentInvites.statusPending';
  }
}

/** Tailwind color class for a status badge. */
function statusColor(status: SentAdvisorInvite['status']): string {
  switch (status) {
    case 'accepted':
      return 'text-[var(--tone-success-fg)] bg-[var(--tone-success-bg)]';
    case 'declined':
      return 'text-[var(--tone-danger-fg)] bg-[var(--tone-danger-bg)]';
    case 'expired':
      return 'text-muted bg-surface-inset';
    default:
      return 'text-[var(--tone-warning-fg)] bg-[var(--tone-warning-bg)]';
  }
}

/** Icon for a status badge. */
function StatusIcon({ status }: { status: SentAdvisorInvite['status'] }) {
  switch (status) {
    case 'accepted':
      return <IconCheck size={12} className="mr-1 inline" />;
    case 'declined':
      return <IconClose size={12} className="mr-1 inline" />;
    case 'expired':
      return <IconHourglass size={12} className="mr-1 inline" />;
    default:
      return <IconHourglass size={12} className="mr-1 inline" />;
  }
}

const SentInvitesPanel: React.FC<SentInvitesPanelProps> = ({ refreshKey }) => {
  const { t, i18n } = useTranslation('common');

  const [invites, setInvites] = useState<SentAdvisorInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    // Guard inside the callback so hooks (useState/useCallback/useEffect) are
    // always called unconditionally — Rules of Hooks compliant.
    if (!ADVISOR_EMAIL_INVITE_ENABLED) return;
    setLoading(true);
    setError(null);
    try {
      const result = await listMyAdvisorInvites();
      setInvites(result.invites);
    } catch (err) {
      logger.error('[SentInvitesPanel] Failed to load sent invitations', err);
      setError(t('tenant.sentInvites.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void load(); }, [refreshKey, load]);

  // Flag-off: render nothing (no Callable was made, loading stays true → hide).
  if (!ADVISOR_EMAIL_INVITE_ENABLED) {
    return null;
  }

  return (
    <section className="mt-4 rounded-lg bg-surface-raised ring-1 ring-black ring-opacity-5">
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        <IconMailOpen className="text-accent-ink shrink-0" />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
          {t('tenant.sentInvites.title')}
        </h2>
      </div>

      {loading && (
        <p className="px-4 pb-4 text-sm text-faint">{t('tenant.sentInvites.loading')}</p>
      )}

      {!loading && error && (
        <p className="px-4 pb-4 text-sm text-[var(--tone-danger-fg)]">{error}</p>
      )}

      {!loading && !error && invites.length === 0 && (
        <p className="px-4 pb-4 text-sm text-faint">{t('tenant.sentInvites.empty')}</p>
      )}

      {!loading && !error && invites.length > 0 && (
        <ul className="divide-y divide-[color:var(--border-hairline)]">
          {invites.map((invite, idx) => {
            // Null-Check auf der ROHEN ms-Quelle, nicht auf dem formatierten String —
            // der zentrale `formatDate` liefert für fehlendes Datum bereits '—', nie `null`.
            const sentDate = invite.invitedAtMs != null ? formatDate(invite.invitedAtMs, { locale: i18n.language }) : null;
            return (
              <li key={`${invite.email ?? 'unknown'}-${idx}`} className="px-4 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-secondary truncate">
                    {invite.email ?? <span className="italic text-faint">{t('tenant.advisorFallback')}</span>}
                  </p>
                  {sentDate && (
                    <p className="text-xs text-faint mt-0.5">
                      {t('tenant.sentInvites.invitedOn', { date: sentDate })}
                    </p>
                  )}
                </div>
                <span
                  className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(invite.status)}`}
                >
                  <StatusIcon status={invite.status} />
                  {t(statusKey(invite.status))}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default SentInvitesPanel;
